import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import "../styles/dispatches-panel.css";
import type { Doc, Id } from "../../convex/_generated/dataModel";

type DispatchStatusFilter = "all" | "active" | "completed" | "cancelled";

const DISPATCH_STATUS_OPTIONS: Array<{ value: DispatchStatusFilter; label: string }> = [
  { value: "all",       label: "All" },
  { value: "active",    label: "Active" },
  { value: "completed", label: "Completed" },
  { value: "cancelled", label: "Cancelled" },
];

type DispatchDoc = Doc<"dispatches">;

function fmtDurationShort(ms: number | null | undefined): string {
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms < 0) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

function fmtClock(ms: number | null | undefined): string {
  if (typeof ms !== "number" || !Number.isFinite(ms) || ms < 0) return "—";
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad2 = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${m}:${pad2(s)}`;
}

function fmtTime(ts: number | null | undefined): string {
  if (typeof ts !== "number" || !Number.isFinite(ts)) return "—";
  return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}

function fmtDate(ts: number | null | undefined): string {
  if (typeof ts !== "number" || !Number.isFinite(ts)) return "—";
  return new Date(ts).toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function statusLabel(status: string): string {
  switch (status) {
    case "traveling":
      return "Traveling";
    case "on_site":
      return "On site";
    case "completed":
      return "Completed";
    case "cancelled":
      return "Cancelled";
    default:
      return status || "Unknown";
  }
}

function formatDispatchVehicleLabel(vehicleName?: string, driverName?: string) {
  const base = (vehicleName || "").trim();
  const driver = (driverName || "").trim();
  if (driver) return driver;
  return base || "Vehicle";
}

export interface DispatchesPanelProps {
  active: DispatchDoc[];
  history: DispatchDoc[];
  focusedDispatchId: Id<"dispatches"> | null;
  onFocusDispatch: (dispatchId: Id<"dispatches">) => void;
  onReplayDispatch: (dispatchId: Id<"dispatches">) => void;
  onCancelDispatch?: (dispatchId: Id<"dispatches">) => void;
  onDeleteReplayData?: (dispatchId: Id<"dispatches">) => void;
}

export default function DispatchesPanel(props: DispatchesPanelProps) {
  const active = props.active || [];
  const history = props.history || [];

  const [statusFilter, setStatusFilter] = useState<DispatchStatusFilter>("all");
  const [searchQuery, setSearchQuery] = useState("");

  const merged = useMemo(() => {
    const all = Array.from(new Map([...history, ...active].map(d => [d._id, d])).values());
    const filtered = all.filter((d) => {
      if (statusFilter === "all") return true;
      if (statusFilter === "active") return d.status === "traveling" || d.status === "on_site";
      if (statusFilter === "completed") return d.status === "completed";
      if (statusFilter === "cancelled") return d.status === "cancelled";
      return true;
    });
    const q = searchQuery.trim().toLowerCase();
    const searched = q
      ? filtered.filter((d) => {
          const vehicle = (d.vehicleName || "").toLowerCase();
          const driver = (d.driverName || "").toLowerCase();
          const site = (d.siteName || "").toLowerCase();
          return vehicle.includes(q) || driver.includes(q) || site.includes(q);
        })
      : filtered;
    const sorted = searched.sort((a, b) => {
      const live = (d: DispatchDoc) => Number(d.status === "traveling" || d.status === "on_site");
      return live(b) - live(a) || (b.startedAt ?? 0) - (a.startedAt ?? 0);
    });
    return sorted.slice(0, 200);
  }, [active, history, statusFilter, searchQuery]);

  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number; id: Id<"dispatches"> } | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!ctxMenu) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node | null;
      if (t && menuRef.current?.contains(t)) return;
      setCtxMenu(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setCtxMenu(null);
    };
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("keydown", onKey, true);
    };
  }, [ctxMenu]);


  return (
    <div className="dispatches-panel">
      <div className="dispatches-toolbar">
        <div className="dispatches-search-shell">
          <div className="dispatches-search-input-wrap">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="1.7"/><path d="m16 16 4.5 4.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round"/></svg>
            <input className="dispatches-search-input" type="search" placeholder="Search dispatches…"
              aria-label="Search dispatches by site, guard or vehicle" value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)} />
          </div>
        </div>
        <div className="dispatch-filters" role="group" aria-label="Filter dispatches by status">
          {DISPATCH_STATUS_OPTIONS.map(option => <button type="button" key={option.value}
            aria-pressed={statusFilter === option.value} onClick={() => setStatusFilter(option.value)}>{option.label}</button>)}
        </div>
      </div>

      {merged.length === 0 ? (
        <div className="dispatches-empty">
          <strong>{searchQuery || statusFilter !== "all" ? "No matching dispatches" : "No dispatches yet"}</strong>
          <p>{searchQuery || statusFilter !== "all" ? "Try another site, guard or status." : "Dispatch activity will appear here when a vehicle is assigned."}</p>
          {searchQuery || statusFilter !== "all" ? <button type="button" onClick={() => { setSearchQuery(""); setStatusFilter("all"); }}>Clear filters</button> : null}
        </div>
      ) : (
        <>
          {merged.map((d, index) => {
            const entryTime = d.startedAt;
            const arriveTime = d.arrivedAt ?? null;
            const doneTime = d.completedAt ?? d.cancelledAt ?? null;
            const isRspndr = d.dispatchType === "rspndr";

            const travelMs = typeof arriveTime === "number" ? arriveTime - entryTime : null;
            const onSiteMs =
              typeof doneTime === "number" && typeof arriveTime === "number"
                ? doneTime - arriveTime
                : d.status === "on_site" && typeof arriveTime === "number"
                  ? Date.now() - arriveTime
                  : null;
            const totalMs =
              typeof doneTime === "number"
                ? doneTime - entryTime
                : d.status === "traveling" || d.status === "on_site"
                  ? Date.now() - entryTime
                  : null;

            const isFocused = props.focusedDispatchId === d._id;
            const statusCls =
              d.status === "traveling" || d.status === "on_site"
                ? "active"
                : d.status === "completed"
                  ? "completed"
                  : "cancelled";
            const isActiveDispatch = d.status === "traveling" || d.status === "on_site";

            const previous = merged[index - 1];
            const previousActive = previous && (previous.status === "traveling" || previous.status === "on_site");
            const showHeading = !previous || previousActive !== isActiveDispatch || (!isActiveDispatch && fmtDate(previous.startedAt) !== fmtDate(entryTime));

            return (
              <section className="dispatch-record" key={d._id}>
                {showHeading ? <div className="dispatch-group-heading"><h2>{isActiveDispatch ? "In progress" : fmtDate(entryTime)}</h2>{isActiveDispatch ? <span className="dispatch-live-indicator">Live</span> : null}</div> : null}
              <div
                key={d._id}
                className={`dispatch-card dispatch-card-${statusCls} ${isFocused ? "dispatch-card-focused" : ""}`}
                role="button"
                tabIndex={0}
                aria-label={`Replay dispatch to ${d.siteName || "Unknown Site"} by ${formatDispatchVehicleLabel(d.vehicleName, d.driverName)}`}
                onClick={() => props.onReplayDispatch(d._id)}
                onContextMenu={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setCtxMenu({ x: e.clientX, y: e.clientY, id: d._id });
                }}
                onKeyDown={(e) => {
                  if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    props.onReplayDispatch(d._id);
                  }
                }}
              >
                <div className="dispatch-card-header">
                  <div className="dispatch-card-header-left">
                    <div className="dispatch-card-name">{formatDispatchVehicleLabel(d.vehicleName, d.driverName)}</div>
                    {d.driverName && d.vehicleName ? <div className="dispatch-card-vehicle">{d.vehicleName}</div> : null}
                  </div>
                  <span className={`dispatch-card-badge ${statusCls}`}>{statusLabel(d.status)}</span>
                </div>
                <div className="dispatch-destination">
                  <svg width="14" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 1 1 14 0Z" stroke="currentColor" strokeWidth="1.5"/><circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.5"/></svg>
                  <div className="dispatch-card-location">{d.siteName || "Unknown Site"}</div>
                  {isRspndr ? <span className="dispatch-card-type-badge">RSPNDR</span> : null}
                </div>
                <div className={`dispatch-journey ${arriveTime !== null ? 'has-arrived' : ''} ${doneTime !== null ? 'has-ended' : ''}`} aria-label="Dispatch timeline">
                  <div className="dispatch-milestone reached"><span className="dispatch-milestone-dot" aria-hidden="true"/><span className="dispatch-milestone-time">{fmtTime(entryTime)}</span><span className="dispatch-milestone-label">Dispatched</span></div>
                  <div className={`dispatch-milestone ${arriveTime !== null ? 'reached' : ''}`}><span className="dispatch-milestone-dot" aria-hidden="true"/><span className="dispatch-milestone-time">{fmtTime(arriveTime)}</span><span className="dispatch-milestone-label">Arrived</span></div>
                  <div className={`dispatch-milestone ${doneTime !== null ? 'reached' : ''}`}><span className="dispatch-milestone-dot" aria-hidden="true"/><span className="dispatch-milestone-time">{fmtTime(doneTime)}</span><span className="dispatch-milestone-label">{d.status === 'cancelled' ? 'Cancelled' : 'Finished'}</span></div>
                </div>
                <div className="dispatch-summary">
                  <div className="dispatch-phase-durations">
                    <span><strong>{fmtDurationShort(travelMs)}</strong> response</span>
                    <span><strong>{fmtDurationShort(onSiteMs)}</strong> on site</span>
                  </div>
                  <div className="dispatch-total"><strong>{fmtClock(totalMs)}</strong><span>total</span></div>
                </div>
                  {props.onCancelDispatch && isActiveDispatch ? (
                    <div className="dispatch-card-actions">
                      <button
                        type="button"
                        className="dispatch-card-cancel-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          props.onCancelDispatch?.(d._id);
                        }}
                        title="Cancel dispatch"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : null}
              </div>
              </section>
            );
          })}
        </>
      )}

      {ctxMenu && typeof document !== "undefined"
        ? createPortal(
            <div
              className="dispatch-ctxmenu"
              ref={menuRef}
              style={{ left: ctxMenu.x, top: ctxMenu.y }}
              role="menu"
              aria-label="Dispatch actions"
            >
              <button
                type="button"
                className="dispatch-ctxmenu-item danger"
                role="menuitem"
                onClick={() => {
                  const id = ctxMenu.id;
                  setCtxMenu(null);
                  if (!props.onDeleteReplayData) return;
                  const ok = window.confirm(
                    "Delete this dispatch?\n\nThis will remove this dispatch card and all of the replay data for this dispatch from the database."
                  );
                  if (!ok) return;
                  props.onDeleteReplayData(id);
                }}
              >
                Delete dispatch…
              </button>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
